import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Overline } from "@/components/Reveal";
import { SectionBackdrop } from "@/components/SectionBackdrop";
import { AUDIENCES, PRESTATION_TITLES } from "@/data/content";
import { api, formatApiError } from "@/lib/api";
import { useLang, AUDIENCE_EN, SERVICE_TITLE_EN } from "@/i18n";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 px-0 py-3 text-white placeholder:text-white/30 focus:border-gold focus:ring-0 focus:outline-none transition-colors duration-300";
const labelCls = "block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1";

const useServiceOptions = () => {
  const [options, setOptions] = useState(PRESTATION_TITLES);
  useEffect(() => {
    api.get("/prestations")
      .then((res) => {
        if (res.data.length > 0) setOptions([...res.data.map((p) => p.title), "Autre projet sur mesure"]);
      })
      .catch(() => {});
  }, []);
  return options;
};

const localizeOption = (o, lang) =>
  lang === "en" ? SERVICE_TITLE_EN[o] || AUDIENCE_EN[o] || o : o;

const SelectField = ({ label, value, onChange, options, testid, lang }) => (
  <div>
    <span className={labelCls}>{label}</span>
    <div className="flex flex-wrap gap-2 pt-1">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`text-xs uppercase tracking-[0.12em] px-4 py-2 border transition-colors duration-300 ${
            value === o ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
          }`}
        >
          {localizeOption(o, lang)}
        </button>
      ))}
    </div>
  </div>
);

export const QuoteForm = ({ initialService = "", initialMessage = "" }) => {
  const options = useServiceOptions();
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", audience: "", service: initialService, message: initialMessage, budget: "", desired_date: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialService) setForm((f) => ({ ...f, service: initialService }));
  }, [initialService]);
  useEffect(() => {
    if (initialMessage) setForm((f) => ({ ...f, message: initialMessage }));
  }, [initialMessage]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.audience || !form.service) {
      toast.error(t("f.selectError"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("f.success"));
      setForm({ name: "", email: "", phone: "", audience: "", service: "", message: "", budget: "", desired_date: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="quote-form">
      <div className="grid sm:grid-cols-3 gap-8">
        <div>
          <label htmlFor="q-name" className={labelCls}>{t("f.name")}</label>
          <input id="q-name" data-testid="quote-name-input" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t("f.namePh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="q-email" className={labelCls}>{t("f.email")}</label>
          <input id="q-email" data-testid="quote-email-input" required type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="vous@exemple.fr" className={inputCls} />
        </div>
        <div>
          <label htmlFor="q-phone" className={labelCls}>{t("f.phone")}</label>
          <input id="q-phone" data-testid="quote-phone-input" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="06 00 00 00 00" className={inputCls} />
        </div>
      </div>
      <SelectField label={t("f.youAre")} value={form.audience} testid="quote-audience" lang={lang}
        onChange={(v) => setForm({ ...form, audience: v })} options={AUDIENCES} />
      <SelectField label={t("f.whichService")} value={form.service} testid="quote-service" lang={lang}
        onChange={(v) => setForm({ ...form, service: v })} options={options} />
      <div>
        <label htmlFor="q-msg" className={labelCls}>{t("f.project")}</label>
        <textarea id="q-msg" data-testid="quote-message-input" required rows={4} value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder={t("f.projectPh")}
          className={`${inputCls} resize-none`} />
      </div>
      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label htmlFor="q-budget" className={labelCls}>{t("f.budget")}</label>
          <input id="q-budget" data-testid="quote-budget-input" value={form.budget}
            onChange={(e) => setForm({ ...form, budget: e.target.value })}
            placeholder={t("f.budgetPh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="q-date" className={labelCls}>{t("f.date")}</label>
          <input id="q-date" data-testid="quote-date-input" type="date" value={form.desired_date}
            onChange={(e) => setForm({ ...form, desired_date: e.target.value })}
            className={`${inputCls} [color-scheme:dark]`} />
        </div>
      </div>
      <button type="submit" data-testid="quote-submit-btn" disabled={loading}
        className="bg-gold text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
        {loading ? t("f.sending") : t("f.send")}
      </button>
    </form>
  );
};

export const BookingForm = () => {
  const options = useServiceOptions();
  const { t, lang } = useLang();
  const [date, setDate] = useState(null);
  const [taken, setTaken] = useState([]);
  const [slot, setSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    const d = format(date, "yyyy-MM-dd");
    api.get("/bookings/availability", { params: { date: d } })
      .then((res) => { setSlots(res.data.slots); setTaken(res.data.taken); })
      .catch(() => { setSlots([]); setTaken([]); });
    setSlot("");
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot || !form.service) {
      toast.error(t("f.bookError"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", { ...form, date: format(date, "yyyy-MM-dd"), slot });
      toast.success(t("f.bookSuccess"));
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setDate(null); setSlot(""); setSlots([]); setTaken([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <span className={labelCls}>{t("f.dateLabel")}</span>
          <div className="border border-white/15 p-4 inline-block" data-testid="booking-calendar">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              locale={lang === "en" ? enUS : fr}
              disabled={{ before: new Date() }}
              className="bg-transparent text-white"
            />
          </div>
        </div>
        <div className="space-y-8">
          <div>
            <span className={labelCls}>
              {t("f.slot")} {date && `— ${format(date, lang === "en" ? "MMMM d, yyyy" : "d MMMM yyyy", { locale: lang === "en" ? enUS : fr })}`}
            </span>
            {!date ? (
              <p className="text-white/40 text-sm pt-2">{t("f.pickDate")}</p>
            ) : (
              <div className="flex flex-wrap gap-2 pt-1">
                {slots.map((s) => {
                  const isTaken = taken.includes(s);
                  return (
                    <button key={s} type="button" disabled={isTaken}
                      data-testid={`slot-btn-${s.replace(":", "")}`}
                      onClick={() => setSlot(s)}
                      className={`font-mono text-sm px-5 py-2.5 border transition-colors duration-300 ${
                        isTaken
                          ? "border-white/10 text-white/25 line-through cursor-not-allowed"
                          : slot === s
                            ? "border-gold bg-gold text-black"
                            : "border-white/20 text-white/70 hover:border-gold/60 hover:text-gold"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <SelectField label={t("f.prestation")} value={form.service} testid="booking-service" lang={lang}
            onChange={(v) => setForm({ ...form, service: v })} options={options} />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-8">
        <div>
          <label htmlFor="b-name" className={labelCls}>{t("f.firstName")}</label>
          <input id="b-name" data-testid="booking-name-input" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("f.yourName")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="b-email" className={labelCls}>{t("f.email")}</label>
          <input id="b-email" data-testid="booking-email-input" required type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.fr" className={inputCls} />
        </div>
        <div>
          <label htmlFor="b-phone" className={labelCls}>{t("f.phoneOpt")}</label>
          <input id="b-phone" data-testid="booking-phone-input" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 00 00 00 00" className={inputCls} />
        </div>
      </div>
      <div>
        <label htmlFor="b-msg" className={labelCls}>{t("f.details")}</label>
        <textarea id="b-msg" data-testid="booking-message-input" rows={3} value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder={t("f.detailsPh")} className={`${inputCls} resize-none`} />
      </div>
      <button type="submit" data-testid="booking-submit-btn" disabled={loading}
        className="bg-gold text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
        {loading ? t("f.booking") : t("f.book")}
      </button>
    </form>
  );
};

export const ContactBooking = () => {
  const [tab, setTab] = useState("devis");
  const { t } = useLang();
  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 border-t border-white/10 overflow-hidden">
      <SectionBackdrop images={["/works/work-2.jpg", "/works/work-5.jpg"]} opacity={0.2} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <Overline>{t("ov.contact")}</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight mb-6">
              {t("cb.h2")}
            </h2>
            <p className="text-white/80 leading-relaxed mb-5">
              {t("cb.lead")}
            </p>
            <p className="text-white/60 leading-relaxed mb-10 text-sm">
              {t("cb.para")}
            </p>
            <div className="flex gap-3" role="tablist">
              <button data-testid="tab-devis" role="tab" aria-selected={tab === "devis"}
                onClick={() => setTab("devis")}
                className={`px-6 py-3 text-sm uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  tab === "devis" ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
                }`}>
                {t("cb.tab1")}
              </button>
              <button data-testid="tab-booking" role="tab" aria-selected={tab === "booking"}
                onClick={() => setTab("booking")}
                className={`px-6 py-3 text-sm uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  tab === "booking" ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
                }`}>
                {t("cb.tab2")}
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            {tab === "devis" ? <QuoteForm /> : <BookingForm />}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
